// Component coverage (supabase/migrations/20261027140000_component_coverage.sql).
//
//   list    rpc coverage_page(company, term, statuses, supplier, buyer, procurement, after_code, after_id, 51)
//           keyset (item_code, item_id) on planning_item_coverage_code_idx or
//           _status_idx; per row the item_planning PK. ~1.2 KB per row
//           (26 weekly buckets × 4 series), ~60 KB per page of 50.
//   counts  rpc coverage_status_counts(company) — one index-only group by.
//   detail  rpc item_coverage(company, item) — the run's lines and exceptions.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";

export const coverageStatuses = ["critical", "no_stock", "warning", "watch", "overstock", "ok", "no_demand"] as const;
export type CoverageStatus = (typeof coverageStatuses)[number];

export const WEEKS = 26;
export const COVERAGE_PAGE = 50;

export interface CoverageRow {
  item_id: string;
  item_code: string;
  item_description: string;
  procurement_type: "purchase" | "manufacture";
  uom_code: string | null;
  uom_decimals: number | null;
  supplier_id: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  buyer_id: string | null;
  buyer_name: string | null;
  status: CoverageStatus;
  on_hand: number | string;
  safety_stock: number | string;
  total_demand: number | string;
  avg_daily_demand: number | string;
  days_cover: number | string | null;
  on_order: number | string;
  next_supply_date: string | null;
  first_short_date: string | null;
  lead_time_days: number;
  week_start: string;
  demand: (number | string)[];
  supply: (number | string)[];
  planned: (number | string)[];
  balance: (number | string)[];
}

export interface CoverageParams {
  companyId: string;
  q: string;
  statuses: CoverageStatus[];
  supplierId: string | null;
  buyerId: string | null;
  procurement: "purchase" | "manufacture" | null;
}

export const coverageKeys = {
  all: ["planning", "component-coverage"] as const,
  list: (p: CoverageParams) => [...coverageKeys.all, "list", p] as const,
  counts: (companyId: string) => [...coverageKeys.all, "counts", companyId] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export interface CoveragePage {
  rows: CoverageRow[];
  nextCursor: { code: string; id: string } | null;
}

export async function listCoverage(
  p: CoverageParams,
  cursor: { code: string; id: string } | null,
  signal: AbortSignal,
  size = COVERAGE_PAGE,
): Promise<CoveragePage> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("coverage_page", {
      p_company_id: p.companyId,
      p_term: p.q.trim() || null,
      p_statuses: p.statuses.length > 0 ? p.statuses : null,
      p_supplier_id: p.supplierId,
      p_buyer_id: p.buyerId,
      p_procurement: p.procurement,
      p_after_code: cursor?.code ?? null,
      p_after_id: cursor?.id ?? null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as CoverageRow[];
  const more = rows.length > size;
  const page = more ? rows.slice(0, size) : rows;
  const last = page.at(-1);
  return { rows: page, nextCursor: more && last ? { code: last.item_code, id: last.item_id } : null };
}

export function coverageListQuery(p: CoverageParams) {
  return infiniteQueryOptions({
    queryKey: coverageKeys.list(p),
    queryFn: ({ pageParam, signal }) => listCoverage(p, pageParam, signal),
    initialPageParam: null as { code: string; id: string } | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
    enabled: !!p.companyId,
  });
}

export function coverageCountsQuery(companyId: string) {
  return queryOptions({
    queryKey: coverageKeys.counts(companyId),
    queryFn: async ({ signal }): Promise<Partial<Record<CoverageStatus, number>>> => {
      if (isDemoMode) return {};
      const { data, error } = await db().rpc("coverage_status_counts", { p_company_id: companyId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? {}) as Partial<Record<CoverageStatus, number>>;
    },
    staleTime: 60_000,
    enabled: !!companyId,
  });
}

/** One item's coverage row (deep link to the detail): coverage_page by item, the table's PK. */
export function coverageRowQuery(companyId: string, itemId: string) {
  return queryOptions({
    queryKey: [...coverageKeys.all, "row", companyId, itemId] as const,
    queryFn: async ({ signal }): Promise<CoverageRow | null> => {
      if (isDemoMode) return null;
      const { data, error } = await db()
        .rpc("coverage_page", { p_company_id: companyId, p_item_id: itemId, p_limit: 1 })
        .abortSignal(signal);
      if (error) throw error;
      return ((data ?? []) as CoverageRow[])[0] ?? null;
    },
    staleTime: 60_000,
    enabled: !!companyId && !!itemId,
  });
}

/** Monday of week w after the coverage's first week (ISO date). */
export function weekDate(weekStart: string, w: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7 * w);
  return d.toISOString().slice(0, 10);
}

/** ISO 8601 week number of a date (yyyy-mm-dd). */
export function isoWeek(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

/** Tone of a weekly projected-stock cell. */
export function cellTone(balance: number, safety: number, overstock: boolean): "short" | "low" | "excess" | "ok" {
  if (balance < 0) return "short";
  if (balance < safety) return "low";
  if (overstock) return "excess";
  return "ok";
}

export const statusTone: Record<CoverageStatus, "danger" | "warning" | "info" | "success" | "neutral" | "violet"> = {
  critical: "danger",
  no_stock: "danger",
  warning: "warning",
  watch: "info",
  overstock: "violet",
  ok: "success",
  no_demand: "neutral",
};
